/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, June 30th 2022, 9:59:10 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import { useGlobalScope } from "core/providers";
import { Organization, OrganizationListItem, ORGANIZATION_STATUS } from "lib/schemas";
import { Image } from "ui-widgets";
import { OrganizationStatusChange } from "./OrganizationStatusChange";

interface OSCardProps extends Pick<React.HTMLAttributes<HTMLButtonElement>, "onClick"> {
  actionText: React.ReactNode;
  organization: Organization | OrganizationListItem;
}

export const OrganizationCard = ({ onClick, actionText, organization }: OSCardProps) => {
  const [organizationStatus, setOrganizationStatus] = React.useState(organization.organization_status);

  const onChangeOrganizationStatus = (organization: Organization) =>
    setOrganizationStatus(organization.organization_status);

  const imageComponent = (
    <Image className="image-style-A" src={organization.logo ?? "/img/no-image.png"} alt="Onestop" />
  );

  const canChangeActiveStatus = (["active", "deactivated"] as (keyof typeof ORGANIZATION_STATUS)[]).includes(
    organizationStatus
  );

  const isGloballySelected = useGlobalScope().globalState.selectedOrganization?.id === organization.id;

  return (
    <div className={`os-org-card d-flex ${isGloballySelected ? "selected" : ""} status-${organizationStatus}`}>
      <div className="os-org-card-part1 d-flex">
        <figure className="os-org-image" onClick={onClick}>
          {imageComponent}
        </figure>
        <div className="os-org-content">
          <h4>{organization.name}</h4>
        </div>
      </div>
      <div className="os-org-card-part2 d-flex">
        <div className="os-org-account-status">
          {canChangeActiveStatus ? (
            <Chip label={ORGANIZATION_STATUS[organizationStatus]} className={ORGANIZATION_STATUS[organizationStatus]} />
          ) : (
            <Chip label={ORGANIZATION_STATUS[organizationStatus]} className={ORGANIZATION_STATUS[organizationStatus]} />
          )}
        </div>
        <div className="os-org-cta-status">
          {canChangeActiveStatus ? (
            <Tooltip title="Activate or Deactivate" placement="top" arrow>
              <div>
                <OrganizationStatusChange onChangeOrganizationStatus={onChangeOrganizationStatus} item={organization} />
              </div>
            </Tooltip>
          ) : (
            <Switch size="small" disabled />
          )}
        </div>
        <div className="os-org-cta-switch">
          <Button variant="outlined" onClick={onClick}>
            {actionText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const OrganizationCardSkeleton = () => (
  <div className="os-service-card d-flex">
    <figure className="os-service-image">
      <Skeleton component="div" width="100%" height="100%" sx={{ transform: "scale(1,1)" }} />
    </figure>
    <div className="os-service-content">
      <Skeleton component="h4" width={140} height={23} />
      <div className="os-service-content-cta">
        <Skeleton component="button" width={64} height={28} sx={{ transform: "scale(1,1)" }} />
      </div>
    </div>
  </div>
);
